import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { DataSource } from '../../enums/DataSource';
import { DistanceType } from '../../enums/DistanceType';
import { RecipeOriginType } from '../../enums/RecipeOriginType';
import { RecipeTable } from '../../enums/RecipeTable';
import { RecipeType } from '../../enums/RecipeType';
import { StatePrescriptionIdentification } from '../../enums/StatePrescriptionIdentification';
import { ProfileType } from '../../enums/ProfileType';
import { strings } from '../../resources/strings';
import { CustomerRecipe } from '../../models/CustomerRecipe';
import { DiagnosticInformation } from '../../models/DiagnosticInformation';
import { Frame } from '../../models/Frame';
import { MultifocalInfo } from '../../models/MultifocalInfo';
import { Recipe } from '../../models/Recipe';
import { SaveRecipeInputBase } from '../../models/SaveRecipeInputBase';
import { SaveDiagnosticInformationInputBase } from '../../models/SaveDiagnosticInformationInputBase';
import { ProductRecipeLinkingRequest } from '../../models/ProductRecipeLinkingRequest';
import { LinkedCustomerRecipe } from '../../models/LinkedCustomerRecipe';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { SaveRecipeInput } from '../../services/Recipes/SaveRecipe/SaveRecipeInput';
import { NewLooseCustomerRecipeInput } from '../../services/CustomerRecipes/NewLooseCustomerRecipe/NewLooseCustomerRecipeInput';
import { SaveProductRecipeLinkingRequestInput, SaveProductRecipeLinkingRequestInputItem } from '../../services/RecipeByProducts/SaveProductRecipeLinkingRequest/SaveProductRecipeLinkingRequestInput';
import { Button } from '../../common-components/Button/Button';
import { TabMenu } from '../../common-components/TabMenu/TabMenu';
import { TabItem } from '../../common-components/TabMenu/styles';
import { If } from '../../common-components/If/If';
import { WaitingIcon } from '../../common-components/WaitingIcon/WaitingIcon';
import { TabRecipeLinking } from './TabRecipeLinking/TabRecipeLinking';
import { FormRecipeV2, FormRecipeValues } from './FormRecipeV2/FormRecipeV2';
import { TabRecipeHistory } from './TabRecipeHistory/TabRecipeHistory';
import { MakeLinking } from './MakeLinking/MakeLinking';
import { ContainerButtons } from './styles';

const TabOptions = {
  RecipeLinking: 'tab-recipe-linking',
  RecipeHistory: 'tab-recipe-history'
}

interface IModalRecipesV2Props {
  visible: boolean;
  readonly?: boolean;
  close: (orderId?: number) => void;
  title: string;
  orderId?: number;
  orderNumber?: string;
  orderDataSource?: DataSource;
  customerShopifyId?: number;
  customerId?: number;
}

const ModalRecipesV2: React.FC<IModalRecipesV2Props> = ({
  visible,
  readonly,
  close,
  title,
  orderId,
  orderNumber,
  orderDataSource,
  customerShopifyId,
  customerId
}) => {

  const {session} = useAuth();
  const [recipes, setRecipes] = React.useState<Recipe[]>([]);
  const [linkedCustomerRecipes, setLinkedCustomerRecipes] = React.useState<LinkedCustomerRecipe[]>([]);
  const [customerRecipes, setCustomerRecipes] = React.useState<CustomerRecipe[]>([]);
  const [activeTab, setActiveTab] = React.useState<string>(TabOptions.RecipeLinking);
  const [formRecipeValues, setFormRecipeValues] = React.useState<FormRecipeValues | undefined>(undefined);
  const [formRecipeVisible, setFormRecipeVisible] = React.useState<boolean>(false);
  const [formRecipeTable, setFormRecipeTable] = React.useState<RecipeTable>(RecipeTable.CustomerRecipe);
  const [frames, setFrames] = React.useState<Frame[]>([]);
  const [makeLinkingVisible, setMakeLinkingVisible] = React.useState<boolean>(false);
  const [linkingRequests, setLinkingRequests] = React.useState<ProductRecipeLinkingRequest[]>([]);
  const [loadingRecipesAndLinkedCustomerRecipes, setLoadingRecipesAndLinkedCustomerRecipes] = React.useState<boolean>(true);

  const {
    getOrderRecipesAndLinkedCustomerRecipesService,
    getFramesByOrderService,
    getCustomerRecipeHistoryByIdService,
    alertService,
    toastService,
    saveRecipeService,
    updateRecipeService,
    deleteRecipeService,
    newLooseCustomerRecipeService,
    updateLooseCustomerRecipeService,
    deleteLooseCustomerRecipeService,
    getOrderLinkingRequestsService,
    saveProductRecipeLinkingRequestService,
    getDetailedStatusForOrderRecipeLinkingService,
    requestLensesService,
    checkIfRecipeIsLockedService,
    getPairsFrameLensByOrderService,
    removeProductRecipeLinkingRequestService,
    requestProcessPrescriptionStateService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    if (!visible) return;
    getRecipesAndLinkedCustomerRecipes();
    getCustomerRecipes();
    getOrderLinkingRequests();
    getFrames();
  }, [visible]);

  const body = React.useMemo(() => {
    if (!visible) return null;

    const isEcommerceOrAdmin = session?.user?.profileType === ProfileType.Ecommerce || session?.user?.profileType === ProfileType.Admin;

    if (formRecipeVisible) {
      return <FormRecipeV2
              orderId={orderId}
              orderNumber={orderNumber}
              recipeTable={formRecipeTable}
              session={session}
              frames={frames}
              values={formRecipeValues}
              change={(values) => changeFormRecipeValues(values)}
            />;
    }

    if (makeLinkingVisible) {
      return <MakeLinking
              orderId={orderId}
              customerId={customerId}
              linkingRequests={linkingRequests}
              setLinkingRequests={setLinkingRequests}
            />;
    }

    if (loadingRecipesAndLinkedCustomerRecipes) {
      return <WaitingIcon />;
    }

    return (
      <>
        <TabMenu
          tabs={[
            {
              key: TabOptions.RecipeLinking,
              text: strings.Linking
            },
            {
              key: TabOptions.RecipeHistory,
              text: strings.RecipeHistory
            }
          ]}
          activeKey={activeTab}
          change={(key) => setActiveTab(key)}
        />
        <TabItem>
          <If condition={activeTab === TabOptions.RecipeLinking}>
            <TabRecipeLinking
              recipes={recipes}
              linkedCustomerRecipes={linkedCustomerRecipes}
              readonly={readonly}
              orderDataSource={orderDataSource}
              editRecipe={isEcommerceOrAdmin ? (recipeId, recipeTable) => openFormRecipeToEdit(recipeId, recipeTable) : undefined}
              deleteRecipe={isEcommerceOrAdmin ? (recipeId, recipeTable) => deleteRecipe(recipeId, recipeTable) : undefined}
              deleteLinking={isEcommerceOrAdmin ? (linking) => deleteLinking(linking) : undefined}
            />
          </If>
          <If condition={activeTab === TabOptions.RecipeHistory}>
            <TabRecipeHistory
              recipes={customerRecipes}
              readonly={readonly}
              orderDataSource={orderDataSource}
              editRecipe={isEcommerceOrAdmin ? (recipeId, recipeTable) => openFormRecipeToEdit(recipeId, recipeTable) : undefined}
              deleteRecipe={isEcommerceOrAdmin ? (recipeId, recipeTable) => deleteRecipe(recipeId, recipeTable) : undefined}
            />
          </If>
        </TabItem>
      </>
    );
  }, [
    visible, orderId, orderNumber, readonly, orderDataSource, customerShopifyId,
    session, recipes, customerRecipes, frames, activeTab, formRecipeVisible, formRecipeTable, formRecipeValues,
    makeLinkingVisible, linkingRequests, linkedCustomerRecipes, loadingRecipesAndLinkedCustomerRecipes
  ]);

  const footer = React.useMemo(() => {
    if (!visible || loadingRecipesAndLinkedCustomerRecipes) return null;

    if (makeLinkingVisible && session?.user?.profileType !== ProfileType.Viewer) {
      return (
        <ContainerButtons>
          <Button minWidth={'120px'} marginRight={'5px'} text={strings.Cancel} click={cancelLinking} />
          <Button minWidth={'120px'} marginLeft={'5px'} text={strings.SaveLinking} click={saveLinking} />
        </ContainerButtons>
      );
    }

    if (formRecipeVisible) {
      return (
        <ContainerButtons>
          <Button minWidth={'120px'} marginRight={'5px'} text={strings.Cancel} click={closeFormRecipe} />
          <Button minWidth={'120px'} marginLeft={'5px'} text={strings.SaveRecipe} click={saveFormRecipe} />
        </ContainerButtons>
      );
    }

    if (activeTab === TabOptions.RecipeLinking && session?.user?.profileType !== ProfileType.Viewer) {
      return (
        <ContainerButtons>
          <Button text={strings.MakeLinking} click={makeLinking} />
        </ContainerButtons>
      );
    }

    if (activeTab === TabOptions.RecipeHistory) {
      return (
        <ContainerButtons>
          <Button text={strings.NewRecipe} click={openFormRecipeToInsert} />
        </ContainerButtons>
      );
    }

    return null;
  }, [
    visible, orderId, orderNumber, readonly, orderDataSource, customerShopifyId,
    session, recipes, customerRecipes, frames, activeTab, formRecipeVisible, formRecipeTable, formRecipeValues,
    makeLinkingVisible, linkingRequests, linkedCustomerRecipes, loadingRecipesAndLinkedCustomerRecipes
  ]);

  return (
    <Modal
      size="lg"
      show={visible}
      onHide={() => clickCloseModal()}
      backdrop="static"
      keyboard={false}
      className="ModalRecipesV2"
    >
      <Modal.Header closeButton style={{ padding: '10px 1rem', height: '50px' }}>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', padding: '10px 1rem' }}>
        {body}
      </Modal.Body>

      <Modal.Footer>
        {footer}
      </Modal.Footer>
    </Modal>
  );

  function clickCloseModal() {
    setActiveTab(TabOptions.RecipeLinking);
    setFormRecipeVisible(false);
    setMakeLinkingVisible(false);
    close(orderId);
  }

  function getRecipesAndLinkedCustomerRecipes() {
    if (!orderId) return;
    setLoadingRecipesAndLinkedCustomerRecipes(true);
    getOrderRecipesAndLinkedCustomerRecipesService.handle({orderId: orderId})
      .then(result => {
        setRecipes(result?.value?.recipes || []);
        setLinkedCustomerRecipes(result?.value?.linkedCustomerRecipes || []);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setLoadingRecipesAndLinkedCustomerRecipes(false);
      });
  }
  
  function getCustomerRecipes() {
    if (!customerId || !customerShopifyId) return;
    getCustomerRecipeHistoryByIdService.handle({ customerId: customerId })
      .then(result => {
        const recipeList = result?.value?.recipes || [];
        setCustomerRecipes(recipeList);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      });
  }

  function getOrderLinkingRequests() {
    if (!orderId) return;
    getOrderLinkingRequestsService.handle({orderId: orderId})
      .then(result => {
        setLinkingRequests(result?.value?.linkingRequests || []);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      });
  }

  function getFrames() {
    if (!orderId) return;
    getFramesByOrderService.handle({ orderId: orderId })
      .then(result => setFrames(result?.value?.frames || []));
  }

  function changeFormRecipeValues(values: FormRecipeValues) {
    setFormRecipeValues(values);
  }

  async function openFormRecipeToEdit(recipeId: number, recipeTable: RecipeTable) {
    let newFormRecipeValues: FormRecipeValues|undefined = new FormRecipeValues();
    let diagnosticsInformations: DiagnosticInformation[] = [];
    let multifocalInfo: MultifocalInfo|undefined = undefined;

    if (recipeTable === RecipeTable.Recipe) {
      const recipe = recipes.find(r => r.id === recipeId);
      if (!recipe) return;
      newFormRecipeValues = {...newFormRecipeValues, ...recipe};
      newFormRecipeValues.name = recipe.customerName;
      diagnosticsInformations = recipe.diagnosticsInformations;
      multifocalInfo = recipe.multifocalInfo;

      const detailedStatus = await getDetailedStatusForOrderRecipeLinkingService.handle({orderId: orderId});
      if (!detailedStatus?.value?.canChangeTheOrderRecipe) {
        const message = detailedStatus?.value?.prescriptionStateDescription
              ? `Não é possível editar receita quando o pedido está no estado "${detailedStatus?.value?.prescriptionStateDescription}".`
              : 'No estado corrente do pedido não é possível editar receita.';
        alertService.warning(message);
        return;
      }
    }
    
    if (recipeTable === RecipeTable.CustomerRecipe) {
      const listCustomerRecipes = activeTab === TabOptions.RecipeLinking
        ? linkedCustomerRecipes.map(lcr => lcr.customerRecipe)
        : customerRecipes;
      const recipe = listCustomerRecipes.find(r => r.id === recipeId);
      if (!recipe || !await customerRecipeCanBeUpdated(recipeId)) return;
      newFormRecipeValues = {...newFormRecipeValues, ...recipe};
      diagnosticsInformations = recipe.customerDiagnosticInformations;
      multifocalInfo = recipe.customerMultifocalInfo;
    }

    newFormRecipeValues.recipeType = newFormRecipeValues?.recipeType || RecipeType.SingleVision;
    const nearDiagnostic = diagnosticsInformations?.find(dix => dix.distanceType === DistanceType.Near);
    newFormRecipeValues.nearDiagnostic = nearDiagnostic ? { ...nearDiagnostic } : undefined;
    const farDiagnostic = diagnosticsInformations?.find(dix => dix.distanceType === DistanceType.Far);
    newFormRecipeValues.farDiagnostic = farDiagnostic ? { ...farDiagnostic } : undefined;
    newFormRecipeValues.multifocalInfoIsConfirmed = true;
    newFormRecipeValues.multifocalInfo = multifocalInfo ? { ...multifocalInfo } : undefined;

    setFormRecipeValues(newFormRecipeValues);
    setFormRecipeTable(recipeTable);
    setFormRecipeVisible(true);
  }

  function openFormRecipeToInsert() {
    if (!customerShopifyId) {
      toastService.warning('Consumidor inválido!');
      return false;
    }

    setFormRecipeValues(undefined);
    setFormRecipeTable(RecipeTable.CustomerRecipe);
    setFormRecipeVisible(true);
  }

  function closeFormRecipe() {
    setFormRecipeVisible(false);
    setFormRecipeValues(undefined);
  }

  async function saveFormRecipe() {
    if (!orderId) {
      toastService.warning(strings.UnidentifiedOrderToSaveTheRecipeYouNeedToIdentifyTheOrder);
      return;
    }
    if (!formRecipeValuesIsValid()) {
      alertService.warning(strings.ToSaveTheRecipeItIsNecessaryToAttachTheRecipeFileOrInformTheDiagnostic);
      return;
    }
    if (!formRecipeHeightsIsValid()) {
      return;
    }
    if (!formRecipeMultifocalInfoIsValid()) {
      return;
    }

    if (formRecipeTable === RecipeTable.Recipe) {
      saveRecipe();
      return;
    }
    if (formRecipeTable === RecipeTable.CustomerRecipe) {
      saveLooseCustomerRecipe();
      return;
    }
  }

  function formRecipeValuesIsValid() {
    if (!formRecipeValues) return false;
    if (
      formRecipeValues?.pathToFile ||
      formRecipeValues?.farDiagnostic?.leftSpherical || formRecipeValues?.farDiagnostic?.leftCylindrical ||
      formRecipeValues?.farDiagnostic?.leftAxis || formRecipeValues?.farDiagnostic?.leftNasoPupillaryDistance ||
      formRecipeValues?.farDiagnostic?.rightSpherical || formRecipeValues?.farDiagnostic?.rightCylindrical ||
      formRecipeValues?.farDiagnostic?.rightAxis || formRecipeValues?.farDiagnostic?.rightNasoPupillaryDistance ||
      formRecipeValues?.nearDiagnostic?.leftSpherical || formRecipeValues?.nearDiagnostic?.leftCylindrical ||
      formRecipeValues?.nearDiagnostic?.leftAxis || formRecipeValues?.nearDiagnostic?.leftNasoPupillaryDistance ||
      formRecipeValues?.nearDiagnostic?.rightSpherical || formRecipeValues?.nearDiagnostic?.rightCylindrical ||
      formRecipeValues?.nearDiagnostic?.rightAxis || formRecipeValues?.nearDiagnostic?.rightNasoPupillaryDistance
    ) return true;
    return false;
  }

  function formRecipeHeightsIsValid() {
    const leftEyeHeight = formRecipeValues?.leftEyeHeight ? +formRecipeValues.leftEyeHeight : null;
    const rightEyeHeight = formRecipeValues?.rightEyeHeight ? +formRecipeValues.rightEyeHeight : null;

    const allHeightsAreNull = !leftEyeHeight && !rightEyeHeight;
    if (allHeightsAreNull) return true;

    const informedLeftEyeHeightIsInvalid = leftEyeHeight && (leftEyeHeight < 0 || leftEyeHeight > 99);
    if (informedLeftEyeHeightIsInvalid) {
      toastService.warning(strings.ConfirmIfInformedFieldValueIsCorrect('Altura Pupila OE'));
      return false;
    }

    const informedRightEyeHeightIsInvalid = rightEyeHeight && (rightEyeHeight < 0 || rightEyeHeight > 99);
    if (informedRightEyeHeightIsInvalid) {
      toastService.warning(strings.ConfirmIfInformedFieldValueIsCorrect('Altura Pupila OD'));
      return false;
    }

    return true;
  }

  function formRecipeMultifocalInfoIsValid() {
    const recipeType = formRecipeValues?.recipeType ? +formRecipeValues.recipeType : RecipeType.SingleVision;

    if (recipeType === RecipeType.SingleVision) return true;

    const actualBridge = formRecipeValues?.multifocalInfo?.actualBridge ? +formRecipeValues.multifocalInfo.actualBridge : 0;
    const actualEyeHeight = formRecipeValues?.multifocalInfo?.actualEyeHeight ? +formRecipeValues.multifocalInfo.actualEyeHeight : 0;
    const actualEyeWidth = formRecipeValues?.multifocalInfo?.actualEyeWidth ? +formRecipeValues.multifocalInfo.actualEyeWidth : 0;
    const actualLargestDiagonal = formRecipeValues?.multifocalInfo?.actualLargestDiagonal ? +formRecipeValues.multifocalInfo.actualLargestDiagonal : 0;

    let message = '';

    if (isNaN(actualBridge)) message += (message ? ', ' : '') + ' no campo Ponte';
    if (isNaN(actualEyeWidth)) message += (message ? ', ' : '') + ' no campo Aro';
    if (isNaN(actualEyeHeight)) message += (message ? ', ' : '') + ' no campo Vertical';
    if (isNaN(actualLargestDiagonal)) message += (message ? ', ' : '') + ' no campo Diagonal Maior';

    if (!message) return true;
    
    toastService.warning(`Verifique os valores informados ${message}.`);
    return false;
  }

  async function saveRecipe() {
    const input = await createSaveRecipeInput();

    if (!input) return;

    if (input.recipeId) {
      updateRecipeService.handle(input)
        .then(result => {
          if (!result?.value?.updateRecipeResult) return;
          handleSavedRecipeSuccessfully();
        })
        .catch(() => {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
        });
      return;
    }

    saveRecipeService.handle(input)
      .then(result => {
        if (!result?.value?.saveRecipeResult) return;
        handleSavedRecipeSuccessfully();
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });
  }

  async function saveLooseCustomerRecipe() {
    const input = await createSaveLooseCustomerRecipeInput();

    if (!input) return;

    if (input.customerRecipeId) {
      updateLooseCustomerRecipeService.handle(input)
        .then(result => {
          if (!result?.value?.result) return;
          handleSavedRecipeSuccessfully();
        })
        .catch(() => {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
        });
      return;
    }

    input.origin = RecipeOriginType.Sislog;

    newLooseCustomerRecipeService.handle(input)
      .then(result => {
        if (!result?.value?.result) return;
        handleSavedRecipeSuccessfully();
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });
  }

  async function createSaveRecipeInput(): Promise<SaveRecipeInput | null> {
    return new Promise(async (resolve, reject) => {
      if (!orderId) {
        resolve(null);
        return;
      }

      const inputBase = await createSaveRecipeInputBase();
      if (!inputBase) {
        resolve(null);
        return;
      }

      let input = new SaveRecipeInput();
      input = {...input, ...inputBase};
      input.customerName = formRecipeValues?.name || '';
      input.customerEmail = '';
      input.orderId = orderId;
      input.recipeId = formRecipeValues?.id;

      resolve(input);
    });
  }

  async function createSaveLooseCustomerRecipeInput(): Promise<NewLooseCustomerRecipeInput | null> {
    return new Promise(async (resolve, reject) => {
      if (!customerShopifyId) {
        resolve(null);
        return;
      }

      const inputBase = await createSaveRecipeInputBase();
      if (!inputBase) {
        resolve(null);
        return;
      }
      
      let input = new NewLooseCustomerRecipeInput();
      input = {...input, ...inputBase};
      input.name = formRecipeValues?.name || '';
      input.description = formRecipeValues?.description || '';
      input.customerShopifyId = customerShopifyId;
      input.customerRecipeId = formRecipeValues?.id;

      resolve(input);
    });
  }

  async function createSaveRecipeInputBase(): Promise<SaveRecipeInputBase|null> {
    return new Promise(async (resolve, reject) => {
      const input = new SaveRecipeInputBase();
      input.recipeType = formRecipeValues?.recipeType ? +formRecipeValues.recipeType : RecipeType.SingleVision;
      input.pathToFile = formRecipeValues?.pathToFile || '';
      input.recipeIsApproved = formRecipeValues?.recipeApproved || false;
      input.nasoPupillaryDistanceIsApproved = formRecipeValues?.nasoPupillaryDistanceApproved || false;
      input.userId = session?.user?.id;
      input.diagnosticInformations = [];
      input.multifocalInfo = formRecipeValues?.multifocalInfo;
      if (input.recipeType === RecipeType.Bifocal && !input.multifocalInfo) {
        input.multifocalInfo = new MultifocalInfo();
      }
      if (input.multifocalInfo) {
        input.multifocalInfo.actualBridge = input?.multifocalInfo?.actualBridge
          ? +input?.multifocalInfo?.actualBridge?.toString().trim() || undefined
          : undefined;
        input.multifocalInfo.actualEyeHeight = input?.multifocalInfo?.actualEyeHeight
          ? +input?.multifocalInfo?.actualEyeHeight.toString().trim() || undefined
          : undefined;
        input.multifocalInfo.actualEyeWidth = input?.multifocalInfo?.actualEyeWidth
          ? +input?.multifocalInfo?.actualEyeWidth.toString().trim() || undefined
          : undefined;
        input.multifocalInfo.actualLargestDiagonal = input?.multifocalInfo?.actualLargestDiagonal
          ? +input?.multifocalInfo?.actualLargestDiagonal.toString().trim() || undefined
          : undefined;
      }
      input.leftEyeHeight = formRecipeValues?.leftEyeHeight ? +formRecipeValues.leftEyeHeight : null;
      input.rightEyeHeight = formRecipeValues?.rightEyeHeight ? +formRecipeValues.rightEyeHeight : null;

      let farDiagnostic: SaveDiagnosticInformationInputBase|null = null;
      let nearDiagnostic: SaveDiagnosticInformationInputBase|null = null;

      if (formRecipeValues?.farDiagnostic) {
        if (!formRecipeValues.farDiagnostic.id) {
          formRecipeValues.farDiagnostic.createByName = session?.user?.name || '';
        }
        formRecipeValues.farDiagnostic.updateByName = session?.user?.name || '';
        formRecipeValues.farDiagnostic.diagnosticInformationId = formRecipeValues.farDiagnostic.id;
        formRecipeValues.farDiagnostic.distanceType = DistanceType.Far;
        input.diagnosticInformations.push({ ...formRecipeValues.farDiagnostic });
        farDiagnostic = formRecipeValues.farDiagnostic;
      }

      if (formRecipeValues?.nearDiagnostic) {
        if (!formRecipeValues.nearDiagnostic.id) {
          formRecipeValues.nearDiagnostic.createByName = session?.user?.name || '';
        }
        formRecipeValues.nearDiagnostic.updateByName = session?.user?.name || '';
        formRecipeValues.nearDiagnostic.diagnosticInformationId = formRecipeValues.nearDiagnostic.id;
        formRecipeValues.nearDiagnostic.distanceType = DistanceType.Near;
        input.diagnosticInformations.push({ ...formRecipeValues.nearDiagnostic });
        nearDiagnostic = formRecipeValues.nearDiagnostic;
      }

      if (input.recipeType === RecipeType.Bifocal && (farDiagnostic || nearDiagnostic)) {
        if (!nearDiagnosticBeenProvided(nearDiagnostic)) {
          const alertResult = await alertService
            .confirm('O diagnóstico para perto não foi fornecido. Posso inferir a partir do diagnóstico para longe?');

          if (alertResult.isConfirmed) {
            nearDiagnostic = getInferredNearDiagnosticFromFar(farDiagnostic, nearDiagnostic, input.multifocalInfo);

            const confirmResult = await alertService.confirmHtml(getDiagnosticConfirmationMessage(farDiagnostic, nearDiagnostic));
            if (confirmResult.isDenied) {
              resolve(null);
              return;
            }

            input.diagnosticInformations = input.diagnosticInformations.filter(x => x.distanceType === DistanceType.Far);
            input.diagnosticInformations.push(nearDiagnostic);
          }
        }
      }

      resolve(input);
    });
  }

  function nearDiagnosticBeenProvided(nearDiagnostic?: SaveDiagnosticInformationInputBase | null) {
    return nearDiagnostic?.leftSpherical || nearDiagnostic?.leftCylindrical ||
           nearDiagnostic?.leftAxis || nearDiagnostic?.leftNasoPupillaryDistance ||
           nearDiagnostic?.rightSpherical || nearDiagnostic?.rightCylindrical ||
           nearDiagnostic?.rightAxis || nearDiagnostic?.rightNasoPupillaryDistance;
  }

  function getInferredNearDiagnosticFromFar(
    farDiagnostic?: SaveDiagnosticInformationInputBase | null, nearDiagnostic?: SaveDiagnosticInformationInputBase | null,
    multifocalInfo?: MultifocalInfo
  ): SaveDiagnosticInformationInputBase {
    let value: number | null | undefined = 0;
    const inferredNearDiagnostic = nearDiagnostic ? { ...nearDiagnostic } : new DiagnosticInformation();
    const addField = getDiagnosticDecimalValue(multifocalInfo?.addField);
    const defaultValueForCalibratedNpdInference = -2;

    value = farDiagnostic?.rightSpherical ? getDiagnosticDecimalValue(farDiagnostic?.rightSpherical) + addField : null;
    inferredNearDiagnostic.rightSpherical = getFormattedDiagnosticValue(value);
    inferredNearDiagnostic.rightCylindrical = farDiagnostic?.rightCylindrical;
    inferredNearDiagnostic.rightAxis = farDiagnostic?.rightAxis;
    value = farDiagnostic?.rightNasoPupillaryDistance
      ? getDiagnosticDecimalValue(farDiagnostic?.rightNasoPupillaryDistance) + defaultValueForCalibratedNpdInference
      : null;
    inferredNearDiagnostic.rightNasoPupillaryDistance = getFormattedDiagnosticValue(value);

    value = farDiagnostic?.leftSpherical ? getDiagnosticDecimalValue(farDiagnostic?.leftSpherical) + addField : null;
    inferredNearDiagnostic.leftSpherical = getFormattedDiagnosticValue(value);
    inferredNearDiagnostic.leftCylindrical = farDiagnostic?.leftCylindrical;
    inferredNearDiagnostic.leftAxis = farDiagnostic?.leftAxis;
    value = farDiagnostic?.leftNasoPupillaryDistance
      ? getDiagnosticDecimalValue(farDiagnostic?.leftNasoPupillaryDistance) + defaultValueForCalibratedNpdInference
      : null;
    inferredNearDiagnostic.leftNasoPupillaryDistance = getFormattedDiagnosticValue(value);

    inferredNearDiagnostic.distanceType = DistanceType.Near;

    return inferredNearDiagnostic;
  }

  function getDiagnosticDecimalValue(strintValue?: string | null): number {
    return strintValue ? +strintValue.replaceAll(',', '.') : 0;
  }

  function getFormattedDiagnosticValue(decimalValue?: number | null): string {
    return decimalValue !== undefined && decimalValue !== null ? decimalValue.toFixed(2).toString().replaceAll('.', ',') : '';
  }

  function getDiagnosticConfirmationMessage(farDiagnostic?: SaveDiagnosticInformationInputBase | null, nearDiagnostic?: SaveDiagnosticInformationInputBase | null) {
    const thFixed = (content: string) => `<th style="width:100px;min-width:100px;">${content}</th>`;
    const th = (content: string) => `<th style="width:25%;">${content}</th>`;
    const td = (content: string) => `<td style="border:1px solid #ddd;">${content}</td>`;
    return `
        <table style="width:100%;">
            <thead>
                <tr>
                    ${thFixed('LONGE')}
                    ${th('ESF')}
                    ${th('CIL')}
                    ${th('EIXO')}
                    ${th('DNP')}
                </tr>
            </thead>
            <tbody>
                <tr>
                    ${td('OD')}
                    ${td(farDiagnostic?.rightSpherical || '')}
                    ${td(farDiagnostic?.rightCylindrical || '')}
                    ${td(farDiagnostic?.rightAxis || '')}
                    ${td(farDiagnostic?.rightNasoPupillaryDistance || '')}
                </tr>
                <tr>
                    ${td('OE')}
                    ${td(farDiagnostic?.leftSpherical || '')}
                    ${td(farDiagnostic?.leftCylindrical || '')}
                    ${td(farDiagnostic?.leftAxis || '')}
                    ${td(farDiagnostic?.leftNasoPupillaryDistance || '')}
                </tr>
            </tbody>
        </table>
        <br/>
        <table style="width:100%;">
            <thead>
                <tr>
                    ${thFixed('PERTO')}
                    ${th('ESF')}
                    ${th('CIL')}
                    ${th('EIXO')}
                    ${th('DNP')}
                </tr>
            </thead>
            <tbody>
                <tr>
                    ${td('OD')}
                    ${td(nearDiagnostic?.rightSpherical || '')}
                    ${td(nearDiagnostic?.rightCylindrical || '')}
                    ${td(nearDiagnostic?.rightAxis || '')}
                    ${td(nearDiagnostic?.rightNasoPupillaryDistance || '')}
                </tr>
                <tr>
                    ${td('OE')}
                    ${td(nearDiagnostic?.leftSpherical || '')}
                    ${td(nearDiagnostic?.leftCylindrical || '')}
                    ${td(nearDiagnostic?.leftAxis || '')}
                    ${td(nearDiagnostic?.leftNasoPupillaryDistance || '')}
                </tr>
            </tbody>
        </table>
        <br/>
        <p><strong>Confirma o diagnóstico acima?</strong></p>
        `;
  }

  function handleSavedRecipeSuccessfully() {
    setFormRecipeVisible(false);
    setFormRecipeValues(undefined);
    getRecipesAndLinkedCustomerRecipes();
    getCustomerRecipes();
  }

  async function deleteRecipe(recipeId: number, recipeTable: RecipeTable) {
    if (!recipeId) return;

    if (recipeTable === RecipeTable.Recipe) {
      const detailedStatus = await getDetailedStatusForOrderRecipeLinkingService.handle({orderId: orderId});
      if (!detailedStatus?.value?.canChangeTheOrderRecipe) {
        const message = detailedStatus?.value?.prescriptionStateDescription
              ? `Não é possível excluir receita quando o pedido está no estado "${detailedStatus?.value?.prescriptionStateDescription}".`
              : 'No estado corrente do pedido não é possível excluir receita.';
        alertService.warning(message);
        return;
      }
    } else if (recipeTable === RecipeTable.CustomerRecipe && !await customerRecipeCanBeUpdated(recipeId)) {
      return;
    }

    const confirmation = await alertService.confirm(strings.DoYouReallyWantToDeleteTheRecipe);

    if (!confirmation.isConfirmed) return;

    if (recipeTable === RecipeTable.Recipe) {
      deleteRecipeService.handle({recipeId:recipeId})
        .then(result => {
          if (result?.value?.result) {
            handleDeletedRecipeSuccessfully();
            return;
          }
          alertService.warning('Não foi possível excluir a receita!');
        })
        .catch(() => {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
        });

      return;
    }

    if (recipeTable === RecipeTable.CustomerRecipe) {
      deleteLooseCustomerRecipeService.handle({recipeId:recipeId})
        .then(result => {
          if (result?.value?.result) {
            handleDeletedRecipeSuccessfully();
            return;
          }
          if (result?.value?.message) {
            alertService.warning(result?.value?.message);
          } else {
            alertService.warning('Não foi possível excluir a receita!');
          }
        })
        .catch(() => {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
        });

      return;
    }
  }

  async function customerRecipeCanBeUpdated(customerRecipeId: number) {
    const checkRecipeResult = await checkIfRecipeIsLockedService.handle({customerRecipeId});
    const numberOfOrdersInAdvancedState = checkRecipeResult?.value?.numberOfOrdersInAdvancedState || 0;

    if (numberOfOrdersInAdvancedState === 0) {
      const numberOfLinks = checkRecipeResult?.value?.numberOfLinks || 0;
      if (numberOfLinks > 0) {
        const message = numberOfLinks > 1
            ? `Esta receita possui ${numberOfLinks} vínculos com pedido e por isso não pode ser alterada ou excluída.`
            : `Esta receita possui um vínculo com pedido e por isso não pode ser alterada ou excluída.`;
        alertService.warning(message);

        return false;
      }

      return true;
    }

    const message = numberOfOrdersInAdvancedState > 1
      ? `Esta receita possui ${numberOfOrdersInAdvancedState} vínculos com pedido em estado avançado e por isso não pode ser alterada ou excluída.`
      : `Esta receita possui um vínculo com pedido em estado avançado e por isso não pode ser alterada ou excluída.`;
    alertService.warning(message);

    return false;
  }

  function handleDeletedRecipeSuccessfully() {
    setFormRecipeVisible(false);
    setFormRecipeValues(undefined);
    getRecipesAndLinkedCustomerRecipes();
    getCustomerRecipes();
  }

  async function makeLinking() {
    if (!orderId) return;
    
    if (!customerShopifyId) {
      toastService.warning('Consumidor inválido!');
      return false;
    }

    const detailedStatus = await getDetailedStatusForOrderRecipeLinkingService.handle({orderId: orderId});
    if (!detailedStatus?.value?.canChangeTheOrderRecipe) {
      const message = detailedStatus?.value?.prescriptionStateDescription
              ? `Não é possível realizar vinculação quando o pedido está no estado "${detailedStatus?.value?.prescriptionStateDescription}".`
              : 'No estado corrente do pedido não é possível realizar vinculação.';
      alertService.warning(message);
      return;
    }
    const result = await getOrderLinkingRequestsService.handle({orderId: orderId});
    setLinkingRequests(result?.value?.linkingRequests || []);
    setMakeLinkingVisible(true);
  }

  function cancelLinking() {
    setLinkingRequests([]);
    setMakeLinkingVisible(false);
  }

  async function saveLinking() {
    if (!orderId) return;

    const resultGetPairs = await getPairsFrameLensByOrderService.handle({ orderId: orderId, orderToken: '' });
    if (!resultGetPairs?.value?.pairs || resultGetPairs.value.pairs.length === 0) {
      alertService.warning(strings.TheOrderDoesNotHaveFrameLensPairsToLink);
      return;
    }

    const resultGet = await getOrderLinkingRequestsService.handle({orderId: orderId});
    const originalList = resultGet?.value?.linkingRequests || [];

    if (!reallyNeedToSave(originalList) ||
        !await canDeleteIfNecessary(originalList) ||
        existsAnyLinkingForSameItemsAndDifferentRecipes()
    ) return;

    const input = new SaveProductRecipeLinkingRequestInput();
    input.orderId = orderId;
    input.userId = session?.user?.id;
    input.linkingRequestItems = [];
    input.clientAreaRequisition = false;

    if (linkingRequests.length > 0) {
      input.linkingRequestItems = linkingRequests.map(linkingRequest => {
        const inputItem = new SaveProductRecipeLinkingRequestInputItem();
        inputItem.frameSku = linkingRequest.frameSku;
        inputItem.lensSku = linkingRequest.lensSku;
        inputItem.customerRecipeId = linkingRequest.customerRecipeId;
        inputItem.orderItemIds = linkingRequest.items.map(lri => lri.orderItemId!);
        inputItem.pairNumber = linkingRequest.pairNumber;
        return inputItem;
      });
      input.confirmLinkingRequests = true;
    }

    saveProductRecipeLinkingRequestService.handle(input)
        .then(async resultSave => {
          const messages = resultSave?.value?.messages || [];
          const changes = (resultSave?.value?.deletedLinks||0) + (resultSave?.value?.insertedLinks||0) +
                          (resultSave?.value?.updatedLinks||0) + (resultSave?.value?.confirmedLinks||0);
          if (messages.length > 0) await showReturnedMessagesFromSaveProductRecipeLinkingRequest(messages, changes === 0);
          handleLinkedProductRecipeSuccessfully();
        });
  }

  function reallyNeedToSave(originalList: ProductRecipeLinkingRequest[]) {
    if (originalList.length > 0 || linkingRequests.length > 0) return true;

    alertService.warningHtml(
      'De acordo com as escolhas realizadas não tem nada para salvar. '+
      'Revise as suas escolhas e tente salvar novamente ou clique em Cancelar para sair.');

    return false;
  }

  async function canDeleteIfNecessary(originalList: ProductRecipeLinkingRequest[]) {
    if (originalList.length <= linkingRequests.length) return true;

    const linksToDelete = originalList.length - linkingRequests.length;
    
    let message = linksToDelete > 1 
        ? `Deseja realmente excluir ${linksToDelete} vínculos existentes?`
        : `Deseja realmente excluir um vínculo existente?`;
    
        const confirmedLinks = originalList.reduce((total, lr) => lr.linkConfirmed ? total+1 : total, 0);
    
    if (confirmedLinks > 0) {
      message = confirmedLinks > 1
        ? `Encontrei ${confirmedLinks} vínculos confirmados.<br>${message}`
        : `Encontrei um vínculo confirmado.<br>${message}`;
    }
    
    const confirmation = await alertService.confirmHtml(message);
    
    return confirmation.isConfirmed;
  }

  function existsAnyLinkingForSameItemsAndDifferentRecipes() {
    if (linkingRequests.length < 1) return false;

    const listCustomerRecipeWithOrderItems = linkingRequests.map(lr => ({
      customerRecipeId: lr.customerRecipeId,
      orderItems: lr.items.map(lri => lri.orderItemId).join('-') + '-' + lr.pairNumber
    }));

    const linkingRequestsGroupByOrderItems: {orderItems:string, quantity:number}[] = [];

    listCustomerRecipeWithOrderItems.forEach(xlr => {
      const group = linkingRequestsGroupByOrderItems.find(g => g.orderItems === xlr.orderItems);
      if (group) {
        group.quantity += 1;
      } else {
        linkingRequestsGroupByOrderItems.push({orderItems:xlr.orderItems, quantity:1});
      }
    });

    if (!linkingRequestsGroupByOrderItems.some(g => g.quantity > 1)) return false;

    alertService.warning(
      'Os mesmos itens/produtos não podem permanecer vinculados a mais de uma receita. '+
      'Resolva as vinculações duplicadas e tente salvar novamente.'
    );

    return true;
  }

  async function showReturnedMessagesFromSaveProductRecipeLinkingRequest(messages: string[], warning: boolean) {
    const message = messages.map((msg, idx) => `${idx > 0 ? '<hr>' : ''}`+`${msg}`).join('');
    return await warning
      ? alertService.warningHtml(message)
      : alertService.infoHtml(message);
  }

  async function handleLinkedProductRecipeSuccessfully() {
    setMakeLinkingVisible(false);
    getRecipesAndLinkedCustomerRecipes();

    const detailedStatus = await getDetailedStatusForOrderRecipeLinkingService.handle({orderId: orderId});

    if (detailedStatus && detailedStatus.value &&
        !detailedStatus.value.orderIsClosed &&
        !detailedStatus.value.orderIsCanceled &&
        (!detailedStatus.value.allRecipesAreApproved || !detailedStatus.value.allPrescriptionItemsHaveEnoughLinksToRequestLens) &&
        (!detailedStatus.value.prescriptionStateId || detailedStatus.value.prescriptionStateId < StatePrescriptionIdentification.SolicitarLente)
    ) {
      return alertService.warning(
        `O pedido ainda está no estado "${detailedStatus.value.prescriptionStateDescription}" `+
        `e ainda não temos receitas completamente aprovadas e vinculadas a todos os itens.`
      );
    }

    if (detailedStatus && detailedStatus.value &&
        detailedStatus.value.allRecipesAreApproved &&
        detailedStatus.value.allPrescriptionItemsHaveEnoughLinksToRequestLens &&
        (!detailedStatus.value.prescriptionStateId || detailedStatus.value.prescriptionStateId < StatePrescriptionIdentification.SolicitarLente)
    ) {
      await alertService.info(
        `O pedido ainda está no estado "${detailedStatus.value.prescriptionStateDescription}". `+
        `Todas as receitas do pedido estão completamente aprovadas e todos os itens estão vinculados a alguma receita. `+
        `Neste cenário já é possível "Solicitar Lente".`
      );
      requestLensesService.handle({orderId: orderId, userId: session?.user?.id})
        .then(result => {
          if (!result?.value?.result)
            return toastService.warning(strings.OpsSomethingWrongSendTheData);
          getRecipesAndLinkedCustomerRecipes();
        }).
        catch(() => {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
        });
      return;
    }

    if (detailedStatus && detailedStatus.value && detailedStatus.value.recipes && detailedStatus.value.recipes.length == 0) {
      requestProcessPrescriptionStateService.handle({
        orderId: orderId!,
        userId: session?.user?.id
      }).then(result => {
        if (!result?.value?.result)
          return toastService.warning(strings.OpsSomethingWrongSendTheData);
        getRecipesAndLinkedCustomerRecipes();
      }).catch(() => {
        toastService.warning(strings.OpsSomethingWrongSendTheData);
      });
    }
  }

  async function deleteLinking(linking: ProductRecipeLinkingRequest) {
    if (!orderId) return;
    
    const detailedStatus = await getDetailedStatusForOrderRecipeLinkingService.handle({orderId: orderId});
    if (!detailedStatus?.value?.canChangeTheOrderRecipe) {
      const message = detailedStatus?.value?.prescriptionStateDescription
              ? `Não é possível excluir vínculos quando o pedido está no estado "${detailedStatus?.value?.prescriptionStateDescription}".`
              : 'No estado corrente do pedido não é possível excluir vínculos.';
      alertService.warning(message);
      return;
    }

    const confirmation = await alertService.confirm(`Deseja realmente excluir o vínculo?`);
    if (!confirmation.isConfirmed) return;

    removeProductRecipeLinkingRequestService.handle({
      productRecipeLinkingRequestId: linking.id,
      userId: session?.user?.id,
      processPrescriptionState: true
    }).then(result => {
      if (!result?.value?.result) return toastService.warning(strings.OpsSomethingWrongSendTheData);
      getRecipesAndLinkedCustomerRecipes();
    }).catch(() => {
      toastService.warning(strings.OpsSomethingWrongSendTheData);
    });
  }
}

export { ModalRecipesV2 };